// getDocsFromServer (not getDocs): with an unprimed cache, getDocs resolves to
// an empty snapshot when offline, silently rendering a blank catalog instead
// of surfacing a load error.
import {
  collection,
  doc,
  getDoc,
  getDocsFromServer,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/services/firestore/collections';
import { withIdConverter } from '@/services/firestore/converters';
import type { Service } from '@/services/firestore/types';

const serviceConverter = withIdConverter<Service>();

export const ACTIVE_SERVICES_QUERY_KEY = ['services', 'active'];
export const BOOKABLE_SERVICES_QUERY_KEY = ['services', 'bookable'];

export async function getService(serviceId: string) {
  try {
    const snapshot = await getDoc(
      doc(db, COLLECTIONS.SERVICES, serviceId).withConverter(serviceConverter),
    );

    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error('Error fetching service:', error);
    throw error;
  }
}

export async function listServices() {
  try {
    const servicesQuery = query(
      collection(db, COLLECTIONS.SERVICES).withConverter(serviceConverter),
      orderBy('displayOrder', 'asc'),
    );
    const snapshot = await getDocsFromServer(servicesQuery);

    return snapshot.docs.map((service) => service.data());
  } catch (error) {
    console.error('Error listing services:', error);
    throw error;
  }
}

export async function listActiveServices() {
  try {
    const servicesQuery = query(
      collection(db, COLLECTIONS.SERVICES).withConverter(serviceConverter),
      where('active', '==', true),
      orderBy('displayOrder', 'asc'),
    );
    const snapshot = await getDocsFromServer(servicesQuery);

    return snapshot.docs.map((service) => service.data());
  } catch (error) {
    console.error('Error listing active services:', error);
    throw error;
  }
}

export async function listBookableServices() {
  try {
    const servicesQuery = query(
      collection(db, COLLECTIONS.SERVICES).withConverter(serviceConverter),
      where('active', '==', true),
      where('onlineBookable', '==', true),
      orderBy('displayOrder', 'asc'),
    );
    const snapshot = await getDocsFromServer(servicesQuery);

    return snapshot.docs.map((service) => service.data());
  } catch (error) {
    console.error('Error listing bookable services:', error);
    throw error;
  }
}
